// PDF export of a storage container's contents list — the sheet that gets printed and
// kept in the bag. Modelled on Shelter Stone's own "FIRST AID KIT CONTENTS LIST": the
// logo at the top, the container named in a badge beside the title, then a block per
// pocket listing what belongs in it and how many, and the container's own notes at the
// foot.
//
// It lists *required* quantities, not what is actually held: this is the checklist a
// medic packs against, so current counts, batches and expiry dates are all left out (the
// two Excel exports cover those). The caller supplies the sections already named,
// ordered and flattened — see contentsSections() in pages/InventoryItems.jsx.

import { fileSlug } from "./download";
import { contentsRows } from "./kitList";

// A4 in millimetres, with the same margin as the guideline export.
const PAGE_W = 210;
const PAGE_H = 297;
const MARGIN = 15;
const CONTENT_W = PAGE_W - MARGIN * 2;
const PT_TO_MM = 0.3528;
const LOGO_URL = process.env.PUBLIC_URL + "/ShelterStoneLogo.png";

// Sampled from the logo, so the pocket header bars are the brand green exactly; red for
// the title and a black badge, as on the printed original.
const GREEN = [0, 150, 72];
const RED = [200, 25, 35];
const BORDER = [170, 170, 170];
const MUTED = [110, 110, 110];
const INK = [30, 30, 30];

const TITLE = "CONTENTS LIST";
const NOTES_TITLE = "Notes";
const TITLE_SIZE = 20;
const BADGE_SIZE = 14;
const HEADER_SIZE = 10.5;
const TRAIL_SIZE = 7.5;
const ITEM_SIZE = 9;
// Horizontal padding can be generous, vertical is kept tight: it is paid twice per box and
// there are a lot of boxes, so it is the difference between one sheet and two.
const PAD_X = 2.5;
const PAD_Y = 1.6;
const HEADER_PAD = 2.5;  // around the text in a header bar
const GUTTER = 3;        // between columns of the grid
const ROW_GAP = 2.5;     // between rows
const STACK_GAP = 2;     // between a pocket and a pocket inside it, in the same column
const BULLET = "•  ";

// Point size to line height in mm. Tighter than the other exports: this is a packing list
// that wants to fit on one sheet, not a report to be read at length.
const lineH = (size) => size * PT_TO_MM * 1.18;

// Where a pocket sits in the bag — read off its own name ("Top Left", "Lid Pocket") —
// now lives in kitList.js, because the public contents page needs the same order in a
// single column. This file turns those rows into geometry; it doesn't decide them.

// Load the logo so jsPDF can add it and we know its aspect ratio.
const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

/**
 * Build and download a container's contents list.
 *
 * `name` titles the page and `notes` is that container's own notes field — deliberately
 * only its own, so pockets' and items' notes stay off the sheet. Each section is
 * `{ title, subtitle, depth, items: [{ name, required }] }`, outermost first; `required`
 * may be null, in which case the line is the name alone (as "Basic Thermometer" is on the
 * printed original).
 */
export async function exportContentsPdf({ name, notes, sections }) {
  // Lazy-loaded so jsPDF stays code-split out of the main bundle.
  const { jsPDF } = await import("jspdf");
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  let y = MARGIN;

  const itemLine = lineH(ITEM_SIZE);
  const headingLine = lineH(HEADER_SIZE);
  const boxHeight = (lineCount) => lineCount * itemLine + PAD_Y * 2;
  const headerHeight = (headingCount) => headingCount * headingLine + HEADER_PAD;

  // --- Header ----------------------------------------------------------------------
  // Logo top right, with the title, the date and the container's name stacked down the
  // left beside it rather than underneath — the whole header then costs the height of the
  // logo alone, which is what keeps a full kit list on one sheet.
  const logoW = 65;
  let logoBottom = y;
  try {
    const img = await loadImage(LOGO_URL);
    const logoH = img.width ? (logoW * img.height) / img.width : 24;
    doc.addImage(img, "PNG", PAGE_W - MARGIN - logoW, y, logoW, logoH);
    logoBottom = y + logoH;
  } catch {
    /* no logo available — carry on without it */
  }
  // Room to the left of the logo (all of the width when there is no logo).
  const headerW = logoBottom > y ? CONTENT_W - logoW - 6 : CONTENT_W;

  let ty = y + lineH(TITLE_SIZE);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(TITLE_SIZE);
  doc.setTextColor(RED[0], RED[1], RED[2]);
  doc.text(TITLE, MARGIN, ty);

  ty += lineH(8) + 1;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(MUTED[0], MUTED[1], MUTED[2]);
  doc.text(`Required contents — generated ${new Date().toLocaleDateString()}`, MARGIN, ty);

  // The container's name in a black badge under the title, shrunk only as far as it has to
  // be to sit clear of the logo.
  let badgeSize = BADGE_SIZE;
  doc.setFont("helvetica", "bold");
  const badgeWidth = (text) => {
    doc.setFontSize(badgeSize);
    return doc.getTextWidth(text) + PAD_X * 2;
  };
  const fullBadge = (name || "Container").toUpperCase();
  while (badgeWidth(fullBadge) > headerW && badgeSize > 8) badgeSize -= 1;
  const [badge] = doc.splitTextToSize(fullBadge, headerW - PAD_X * 2);
  const badgeH = lineH(badgeSize) + 2.4;
  ty += 2.5;
  doc.setFillColor(0, 0, 0);
  doc.rect(MARGIN, ty, badgeWidth(badge), badgeH, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(badgeSize);
  doc.text(badge, MARGIN + PAD_X, ty + badgeH - 1.7);

  y = Math.max(ty + badgeH, logoBottom) + 4;

  // --- Blocks ----------------------------------------------------------------------

  // A header bar takes two lines rather than losing the end of a long pocket name. Three
  // would be a naming problem rather than a layout one, so that's the cap.
  const headingFor = (title, width) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(HEADER_SIZE);
    return doc.splitTextToSize(title || "", width - PAD_X * 2).slice(0, 2);
  };

  // A section's bullet lines, wrapped to the width of the column it will sit in — so the
  // same pocket measures differently in a two-column row than in a full-width one.
  const measure = (section, width) => {
    // A pocket name too long for one line of the bar wraps onto a second, as on the
    // printed original — cutting it would lose which pocket the block is.
    const headingLines = headingFor(section.title, width);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(ITEM_SIZE);
    const bulletW = doc.getTextWidth(BULLET);
    const textW = width - PAD_X * 2 - bulletW;
    const lines = section.items.length
      ? section.items.flatMap(it => {
          const label = it.required == null ? it.name : `${it.name} x${it.required}`;
          // The bullet goes on the first line only; the rest hangs under it.
          return doc.splitTextToSize(label, textW).map((text, k) => ({ text, first: k === 0 }));
        })
      : [{ text: "Nothing recorded in here", muted: true }];
    return { lines, bulletW, headingLines, headerH: headerHeight(headingLines.length) };
  };

  // One green header bar and the bordered box of lines under it, at an explicit position
  // and height — the single drawing primitive for pockets and for the notes block. The
  // bar's height is passed in, so every column of a row can share the tallest.
  const drawBox = ({ headingLines, subtitle, headerH, lines, bulletW, x, top, width, boxH }) => {
    doc.setFillColor(GREEN[0], GREEN[1], GREEN[2]);
    doc.rect(x, top, width, headerH, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(HEADER_SIZE);
    // Bottom-aligned in the bar, so a one-line and a two-line heading share a baseline.
    const lastBaseline = top + headerH - HEADER_PAD * 0.7;
    headingLines.forEach((ln, k) => {
      doc.text(ln, x + PAD_X, lastBaseline - (headingLines.length - 1 - k) * headingLine);
    });

    // The trail shares the heading's last line, right-aligned — but only when it fits
    // beside it. In a narrow column it's dropped rather than overprinted; nothing is lost,
    // since a nested pocket is drawn directly under the pocket holding it.
    const lastLineW = doc.getTextWidth(headingLines[headingLines.length - 1] || "");
    doc.setFont("helvetica", "normal");
    doc.setFontSize(TRAIL_SIZE);
    if (subtitle && doc.getTextWidth(subtitle) + 4 <= width - PAD_X * 2 - lastLineW) {
      doc.text(subtitle, x + width - PAD_X, lastBaseline, { align: "right" });
    }

    doc.setDrawColor(BORDER[0], BORDER[1], BORDER[2]);
    doc.setLineWidth(0.2);
    doc.rect(x, top + headerH, width, boxH);
    doc.setFontSize(ITEM_SIZE);
    let ly = top + headerH + PAD_Y;
    for (const ln of lines) {
      ly += itemLine;
      const color = ln.muted ? MUTED : INK;
      doc.setTextColor(color[0], color[1], color[2]);
      doc.setFont("helvetica", ln.muted ? "italic" : "normal");
      if (ln.first) doc.text(BULLET, x + PAD_X, ly);
      doc.text(ln.text, x + PAD_X + bulletW, ly);
    }
  };

  // A block that may outgrow the page: drawn full width, over as many boxes as it takes,
  // each under its own bar. Used for the notes, and for anything too tall to sit in a row.
  const drawFlowing = (title, subtitle, lines, bulletW) => {
    const headingLines = headingFor(title, CONTENT_W);
    const headerH = headerHeight(headingLines.length);

    // Move a block that would only just overrun on whole, rather than splitting it and
    // leaving a line or two stranded overleaf — as long as it fits a page at all.
    const wholeH = headerH + boxHeight(lines.length);
    if (y + wholeH > PAGE_H - MARGIN && wholeH <= PAGE_H - MARGIN * 2 && y > MARGIN) {
      doc.addPage();
      y = MARGIN;
    }

    let cursor = 0;
    let continued = false;
    while (cursor < lines.length) {
      const fit = Math.floor((PAGE_H - MARGIN - y - headerH - PAD_Y * 2) / itemLine);
      // Never leave a header bar stranded at the foot of a page with a line or two under it.
      if (fit < Math.min(3, lines.length - cursor) && y > MARGIN) {
        doc.addPage();
        y = MARGIN;
        continue;
      }
      const slice = lines.slice(cursor, cursor + Math.max(1, fit));
      const boxH = boxHeight(slice.length);
      drawBox({
        headingLines, headerH, lines: slice, bulletW,
        x: MARGIN, top: y, width: CONTENT_W, boxH,
        subtitle: continued ? "continued" : subtitle,
      });
      y += headerH + boxH + ROW_GAP;
      cursor += slice.length;
      continued = true;
    }
  };

  for (const row of contentsRows(sections)) {
    const width = (CONTENT_W - GUTTER * (row.length - 1)) / row.length;
    const cells = row.map(cell => cell.map(section => {
      const { lines, bulletW, headingLines, headerH } = measure(section, width);
      return { section, lines, bulletW, headingLines, headerH, boxH: boxHeight(lines.length) };
    }));
    const topHeaderH = Math.max(...cells.map(cell => cell[0].headerH));
    const cellH = cells.map(cell =>
      cell.reduce((h, b, j) => h + (j === 0 ? topHeaderH : b.headerH) + b.boxH, 0)
      + STACK_GAP * (cell.length - 1));
    const rowH = Math.max(...cellH);

    // Taller than a whole page, so no row can hold it: those sections flow instead.
    if (rowH > PAGE_H - MARGIN * 2) {
      for (const cell of row) {
        for (const section of cell) {
          const { lines, bulletW } = measure(section, CONTENT_W);
          drawFlowing(section.title, section.subtitle, lines, bulletW);
        }
      }
      continue;
    }

    if (y + rowH > PAGE_H - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }

    // Read once into a const: the row's boxes are drawn from callbacks, and closing over
    // the running y would be closing over something that moves under them.
    const rowTop = y;
    cells.forEach((cell, k) => {
      const x = MARGIN + k * (width + GUTTER);
      let top = rowTop;
      cell.forEach((b, j) => {
        // Every column of the row leads with a bar of the same height, so their tops line
        // up even where one pocket's name wraps and another's doesn't. The last box in a
        // column takes up the slack, so the columns end level too — as on the original.
        const headerH = j === 0 ? topHeaderH : b.headerH;
        const boxH = j === cell.length - 1 ? b.boxH + (rowH - cellH[k]) : b.boxH;
        drawBox({
          headingLines: b.headingLines, subtitle: b.section.subtitle, headerH,
          lines: b.lines, bulletW: b.bulletW, x, top, width, boxH,
        });
        top += headerH + boxH + STACK_GAP;
      });
    });
    y += rowH + ROW_GAP;
  }

  // The container's own notes close the sheet — anything the packer needs that isn't a
  // line item ("before you deploy…" and the like).
  if ((notes || "").trim()) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(ITEM_SIZE);
    const lines = notes.replace(/\r/g, "").split("\n")
      .flatMap(para => (para.trim() === "" ? [""] : doc.splitTextToSize(para, CONTENT_W - PAD_X * 2)))
      .map(text => ({ text }));   // no bullets: notes are prose, not a list
    drawFlowing(NOTES_TITLE, "", lines, 0);
  }

  // Footer on every page: "© Shelter Stone <year>" centred, with the page number
  // alongside once the list runs to more than one sheet.
  const year = new Date().getFullYear();
  const total = doc.getNumberOfPages();
  for (let p = 1; p <= total; p += 1) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(`© Shelter Stone ${year}`, PAGE_W / 2, PAGE_H - 8, { align: "center" });
    if (total > 1) {
      doc.text(`Page ${p} of ${total}`, PAGE_W - MARGIN, PAGE_H - 8, { align: "right" });
    }
  }

  doc.save(`${fileSlug(name, "container")}-contents-list.pdf`);
}
